import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import moment from 'moment';

import MenuList from './MenuList';
import CartPanel from './CartPanel';

import axios from '../../../axios-url';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

import { F, raw } from './RestaurantOrderFields';
import { buildStageFields, buildMenuItemRow, buildKotInfoRow } from './RestaurantOrderState';
import {
    updateContainerFieldValue, addContainerRow, removeContainerRow,
} from './containerHelpers';
import { ORDER_TYPES } from '../../Layout/POSSidebar';

const RESTAURANT_ORDER_ID = 'f7405b60-9933-4b08-a8be-107aa21a05f7';
const PROCESS_NUM = '303';
const STAGE_NUM = '001';
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
const QR_ORDER_TYPE = '550e8400-e29b-41d4-a716-446655440001';

// ── Map a KOT action row's real StatusId (RESSTATUS_MASTER, ActionId 1) to
// display info for the Order Status table — this is the actual kitchen
// progression (confirmed by customer -> sent to kitchen -> accepted ->
// preparing -> ready -> closed), not the two-state Pending/Sent draft flag
// on the RequestAttributes menu-item row (MI_KOT_STATUS), which only ever
// tracks "has this device dispatched it locally yet" and says nothing about
// what the kitchen has actually done with it.
const KOT_STATUS_META = {
    10: { label: 'Confirmed', order: 0, className: 'qr-status-pending' },
    11: { label: 'Sent to Kitchen', order: 1, className: 'qr-status-sent' },
    12: { label: 'Accepted', order: 2, className: 'qr-status-sent' },
    13: { label: 'Preparing', order: 3, className: 'qr-status-cooking' },
    14: { label: 'Ready', order: 4, className: 'qr-status-ready' },
    15: { label: 'Closed', order: 5, className: 'qr-status-served' },
    16: { label: 'Paused', order: 6, className: 'qr-status-cooking' },
    17: { label: 'Cancelled', order: 7, className: 'qr-status-closed' },
};

const kotStatusMeta = (statusId) => KOT_STATUS_META[Number(statusId)] || {
    label: 'Unknown', order: 99, className: 'qr-status-pending',
};

// Requestactions rows aren't guaranteed to come back in the same PascalCase
// this file POSTs them as (the backend's read-side casing isn't consistent
// across endpoints — see kotActions.js's own defensive lookups for the same
// reason) — try a few plausible casings rather than assume one.
const pick = (obj, ...keys) => {
    for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    }
    return undefined;
};

// ── Section Component ──────────────────────────────────────────────────
const Section = ({ title, isOpen, onToggle, badge, children }) => (
    <div className="qr-section">
        <button className="qr-section-header" onClick={onToggle}>
            <span className="qr-section-title">{title}</span>
            {badge != null && <span className="qr-section-badge">{badge}</span>}
            <span className={`qr-section-chevron ${isOpen ? 'open' : ''}`}>▾</span>
        </button>
        {isOpen && <div className="qr-section-body">{children}</div>}
    </div>
);

// Per-device display name, for the multi-device "who ordered this" labels
// (MI_ORDERED_BY) — purely a local label, never sent as an identity, so it's
// kept outside redux/localStorage's other auth keys.
const GUEST_NAME_STORAGE_KEY = 'qr_guest_name';

// The name field is optional and most guests will leave it blank — so a bare
// "Guest" fallback would put every device that skips it into one shared
// bucket, defeating the point of scoping the cart per person (see the
// cartItems filters below, and getEffectiveGuestName). This generates a
// short, stable label ("Guest-3F2A") once per device and remembers it, so
// the common "didn't type a name" case still scopes correctly.
const DEVICE_LABEL_STORAGE_KEY = 'qr_guest_device_label';

// The whole guest-identity bundle (typed name, auto label, lock) is only
// meant to last one dining visit. Neither tableId nor requestId make a
// reliable expiry key on their own — a table gets reused by every customer
// who ever sits there, and there's no push signal to this device telling it
// when an order actually closes (and guessing at the order-level status
// field's shape from the GET response isn't something to build on without
// confirming it against a real response first). A flat timeout sidesteps
// both problems: it doesn't depend on any backend signal at all, just on
// "this device hasn't touched a guest identity in a while."
const GUEST_IDENTITY_TIMESTAMP_KEY = 'qr_guest_identity_ts';
const GUEST_IDENTITY_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours — generous for a single visit

const clearStaleGuestIdentity = () => {
    const ts = Number(localStorage.getItem(GUEST_IDENTITY_TIMESTAMP_KEY) || 0);
    if (!ts || Date.now() - ts > GUEST_IDENTITY_TTL_MS) {
        localStorage.removeItem(GUEST_NAME_STORAGE_KEY);
        localStorage.removeItem(DEVICE_LABEL_STORAGE_KEY);
        localStorage.removeItem(GUEST_IDENTITY_TIMESTAMP_KEY);
    }
};

const getOrCreateDeviceLabel = () => {
    clearStaleGuestIdentity();

    let label = localStorage.getItem(DEVICE_LABEL_STORAGE_KEY);
    if (!label) {
        label = 'Guest-' + Math.random().toString(36).slice(2, 6).toUpperCase();
        localStorage.setItem(DEVICE_LABEL_STORAGE_KEY, label);
        // Stamps the start of this identity's window — deliberately not
        // refreshed on every later touch (typing a name, adding items);
        // the TTL is generous enough that a fixed "since first seen this
        // visit" window is simpler and enough.
        localStorage.setItem(GUEST_IDENTITY_TIMESTAMP_KEY, String(Date.now()));
    }
    return label;
};

class QrOrderDetails extends Component {
    state = {
        stageFields: buildStageFields(),
        DynamicRequestID: null,
        loading: false,
        loadOnce: true,
        reqStatus: null,
        requestNum: null,
        paymentEntries: [],
        tableId: null,
        tableNo: null,
        requestActionsRaw: [],
        cartItems: [],
        // Evaluated first — clears a stale (>6h old) guest identity before
        // guestName/nameLocked below read whatever's left in localStorage.
        // Object literal properties evaluate top-to-bottom, so order here
        // is load-bearing, not cosmetic.
        deviceLabel: getOrCreateDeviceLabel(),
        guestName: localStorage.getItem(GUEST_NAME_STORAGE_KEY) || '',
        // Locked once a name was already committed on a previous visit —
        // stays locked across reloads. Also locks the moment this device
        // starts ordering (see isNameLocked below), even if never typed.
        nameLocked: Boolean(localStorage.getItem(GUEST_NAME_STORAGE_KEY)),


        // ── Section toggle states ──
        sections: { status: true, menu: false, pay: false },

        orderConfirmed: false,
        hasUnconfirmedChanges: false,
        isManualFetch: false,
        isOrderLocked: false,
        shouldShowInList: false,
        isSaving: false,
    };

    // Every device on this shared order gets asked once for a display name
    // (optional — falls back to this device's stable auto-generated label).
    // Never blocks ordering.
    setGuestName = (name) => {
        localStorage.setItem(GUEST_NAME_STORAGE_KEY, name);
        this.setState({ guestName: name });
    };

    // Finalizes whatever's typed once the guest is done editing it (blur) —
    // once committed, the name can't be changed again. Changing it after
    // items exist would silently detach already-added pending items from
    // view, since the cartItems filters match on the live effective name.
    commitGuestName = () => {
        if (this.state.guestName.trim()) {
            this.setState({ nameLocked: true });
        }
    };

    // Locked once explicitly committed (typed a name, then moved on) OR the
    // moment this device has actually added something to the order — at
    // that point it's been "assigned" whatever name/fallback label was in
    // effect, and changing it now would orphan those items.
    isNameLocked = () => this.state.nameLocked || this.state.cartItems.length > 0;

    // The value actually stamped onto MI_ORDERED_BY and used to scope the
    // cart to this device — a typed name if given, else the stable per-
    // device fallback label (never a bare "Guest" shared by everyone who
    // skips the field).
    getEffectiveGuestName = () => this.state.guestName || this.state.deviceLabel;

    toggleSection = (key) => {
        this.setState(prev => ({
            sections: { ...prev.sections, [key]: !prev.sections[key] },
        }));
    };

    componentWillUnmount() {
        if (this._pollTimer) clearInterval(this._pollTimer);
    }

    // Lets a second device see items another device just added without a
    // manual refresh — not required for correctness (the shared requestId
    // already means every device converges next time it loads), just makes
    // it live. Skipped while this device has its own unsaved local changes,
    // so a customer mid-cart-edit never has their not-yet-saved additions
    // silently wiped out by a poll's rehydration.
    startLivenessPoll = () => {
        this._pollTimer = setInterval(() => {
            const { DynamicRequestID, hasUnconfirmedChanges, isSaving } = this.state;
            if (DynamicRequestID && !hasUnconfirmedChanges && !isSaving) {
                this.fetchOrderDetails(DynamicRequestID);
            }
        }, 20000);
    };

    componentDidMount() {


        console.log("getMasterKeywordList is", this.props.getMasterKeywordList);

        this.startLivenessPoll();

        const { tableId, tableNo, requestId } = this.props.match.params;

        this.setState({ tableId, tableNo, cartItems: [] }, () => {
            const lists = ['MenuCategory', 'UOM', 'Kitchen', 'Modifier'];
            lists.forEach(l => {
                if (this.props.getMasterKeywordList) {
                    this.props.getMasterKeywordList(l);

                }
            });


            this.props.getMasterData(
                "res/GetinvMenuItemList?companyId=" +
                this.props.loggedUserDetail.CompanyId
            );

            if (requestId) {
                this.setState({
                    loading: true,
                    loadOnce: true,
                    isManualFetch: true,
                    shouldShowInList: true,
                });
                this.fetchOrderDetails(requestId);
            } else {
                this.fetchExistingOrderForTable();
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.tableLists &&
            this.props.tableLists.requestAttribute1 !== undefined &&
            this.props.tableLists !== prevProps.tableLists
        ) {
            const newId = this.props.tableLists.requestId || this.props.tableLists.requestID;
            const currentId = this.state.DynamicRequestID;
            if (newId !== currentId) {
                this.rehydrateFromResponse(this.props.tableLists);
            }
        }
    }

    fetchOrderDetails = (requestId) => {
        if (!requestId) return;
        this.props.getMasterTableList(
            'Employee/RequestAttributesDetails?Id=' + requestId
        );
    };

    rehydrateFromResponse = (data) => {
        console.log('Rehydrating from response:', data);

        let rehydratedFields = buildStageFields();

        if (data.requestAttribute1 && data.requestAttribute1.length > 0) {
            const stageList = data.requestAttribute1[0];
            const stageFields = stageList.stage_Field || [];

            stageFields.forEach(field => {
                const fieldsY = 'field_' + field.fieldName;

                let fieldValue = field.fieldValue;
                if (field.fieldType === 8 || field.fieldType === '8') {
                    fieldValue = new Date(field.fieldValue);
                } else if (field.fieldType === 10 || field.fieldType === '10') {
                    fieldValue = field.fieldValue === 'true';
                }

                if (rehydratedFields[fieldsY] !== undefined) {
                    rehydratedFields[fieldsY] = {
                        ...rehydratedFields[fieldsY],
                        FieldValue: fieldValue,
                        FieldId: field.fieldId || '',
                    };
                }

                if (field.stage_FieldsList && field.stage_FieldsList.length > 0) {
                    const containerRows = field.stage_FieldsList.map(conFields => {
                        const rowObj = {};

                        (conFields.stage_FieldsListc2 || []).forEach(conField => {
                            const childKey = 'field_' + conField.fieldNum;

                            let childValue = conField.fieldValue;
                            if (conField.fieldType === 8 || conField.fieldType === '8') {
                                childValue = new Date(conField.fieldValue);
                            } else if (conField.fieldType === 10 || conField.fieldType === '10') {
                                childValue = conField.fieldValue === 'true';
                            }

                            rowObj[childKey] = {
                                FieldName: conField.fieldNum,
                                FieldLabel: conField.fieldDescription,
                                FieldValue: childValue,
                                FieldType: conField.fieldType,
                                DataType: conField.dataType,
                                ParentFieldId: conField.parentFieldNum,
                                ChildRowNmbr: conField.childRowNmbr,
                                FieldId: conField.fieldId || '',
                                type: conField.containerType,
                            };
                        });

                        if (field.fieldDescription === 'Menu Items') {
                            const menuItemId = conFields.stage_FieldsListc2?.find(
                                f => f.fieldDescription === 'Menu Item'
                            )?.fieldValue;

                            if (menuItemId) {
                                const menuItemsList = this.props.menuitems || [];
                                const found = menuItemsList.find(
                                    m => m.menuItemId === menuItemId
                                );
                                rowObj.__displayName = found
                                    ? found.menuName
                                    : menuItemId;
                            }
                        }
                        return rowObj;
                    });

                    rehydratedFields[fieldsY] = {
                        ...rehydratedFields[fieldsY],
                        ContainerFields: containerRows,
                    };
                }
            });
        }

        const requestId = data.requestId || data.requestID || data.requestid || null;

        if (data.requestNum && rehydratedFields[F.ORDER_NUMBER] !== undefined) {
            rehydratedFields[F.ORDER_NUMBER] = {
                ...rehydratedFields[F.ORDER_NUMBER],
                FieldValue: data.requestNum,
            };
        }

        // const allRows = rehydratedFields[F.MENU_ITEMS]?.ContainerFields || [];
        const allRows = rehydratedFields[F.MENU_ITEMS]?.ContainerFields || [];

        allRows.forEach(row => {
            row.__isNew = false;
            // Same reasoning as extractMenuItemRows — anything already
            // beyond Pending on the server already has a KOT action row;
            // without this a fresh load/reload would let 'confirm' try to
            // recreate one.
            const kotStatus = row[F.MI_KOT_STATUS]?.FieldValue;
            row.__kotActionSent = kotStatus && kotStatus !== 'Pending';
        });

        // Only this device's own still-pending rows — same scoping (and same
        // helper) as addToCart/updateQty/removeFromCart use.
        const myName = this.getEffectiveGuestName();
        const cartItems = this.buildCartItemsFor(rehydratedFields, myName);

        console.log('All rows from rehydration:', allRows);

        const hasAnyItems = allRows.length > 0;

        const hasConfirmedItems = allRows.some(row => {
            const status = row[F.MI_KOT_STATUS]?.FieldValue;
            return status && status !== 'Pending';
        });

        const isOrderLocked = hasConfirmedItems;

        this.setState({
            loadOnce: false,
            loading: false,
            stageFields: rehydratedFields,
            DynamicRequestID: requestId,
            reqStatus: data.status,
            requestNum: data.requestNum,
            orderConfirmed: hasAnyItems,
            hasUnconfirmedChanges: false,
            activeTab: hasAnyItems ? 'status' : 'add',
            sections: hasAnyItems
                ? { status: true, menu: false, pay: false }
                : { status: false, menu: true, pay: false },
            requestActionsRaw: data.requestactions || [],
            isManualFetch: false,
            isOrderLocked: isOrderLocked,
            shouldShowInList: true,
            // cartItems: [],
            cartItems: cartItems,
        });
    };

    fetchExistingOrderForTable = () => {
        const { tableId, tableNo } = this.state;
        if (!tableId) {
            console.error('QrOrderDetails: missing tableId in URL');
            return;
        }

        axios.get(
            "/master/api/res/GetrestableOrderNum?BranchId=00000000-0000-0000-0000-000000000001" +
            "&CompanyId=" + this.props.loggedUserDetail.CompanyId
        )
            .then(response => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    // GetrestableOrderNum rows carry tableNumber, not tableId
                    // (confirmed against the real response shape — there's no
                    // tableId field on these rows at all) — matching on
                    // tableId here always missed, so claimTable() fired an
                    // Insert on every visit even when the table already had a
                    // running order.
                    const existing = response.data.find(o => String(o.tableNumber) === String(tableNo));

                    if (existing && existing.requestId) {
                        this.setState({
                            loading: true,
                            loadOnce: true,
                            shouldShowInList: true,
                        });
                        this.props.getMasterTableList(
                            'Employee/RequestAttributesDetails?Id=' + existing.requestId
                        );
                    } else {
                        this.claimTable();
                    }
                } else {
                    this.claimTable();
                }
            })
            .catch(err => {
                console.error('Failed to check running orders for table', err);
                this.claimTable();
            });
    };

    claimTable = () => {
        const { tableId, tableNo } = this.state;

        this.setState(prev => {
            let freshFields = buildStageFields();

            if (freshFields[F.TABLE_INFO]?.ContainerFields?.[0]) {
                freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_NUMBER] = {
                    ...freshFields[F.TABLE_INFO].ContainerFields[0][F.TABLE_NUMBER],
                    FieldValue: tableNo,
                };
                // No F.TABLE_ID exists in RestaurantOrderFields.js — there is no
                // table-GUID field in the process definition today, only
                // TABLE_NUMBER. tableId still drives the shared-order lookup
                // (fetchExistingOrderForTable, keyed off tableId against
                // GetrestableOrderNum), it's just never persisted into
                // RequestAttributes itself.
            }

            freshFields[F.ORDER_TYPE] = {
                ...freshFields[F.ORDER_TYPE],
                FieldValue: QR_ORDER_TYPE,
            };

            return { stageFields: freshFields };
        }, () => {
            this.saveRequest('submit', 'table');
        });
    };

    // MI_MODIFIER_SEL is a type-3 (MULTIDROPDOWN) field — buildFieldValue's
    // serializeFieldValue JSON.stringifies array FieldValues for the save
    // payload (this schema's save endpoint requires that field to go over
    // the wire as "[]"/'["id1","id2"]', not a plain string — see the same
    // constraint documented in RestaurantOrder.js's buildFieldValue), so a
    // row's MI_MODIFIER_SEL is always kept as a real array in stageFields.
    // On the way back, though, the backend echoes whatever string it stored
    // (rehydrated rows, another device's rows, this order's own action rows
    // once confirmed), so this normalizes array / JSON-string / plain
    // comma-string alike before resolving ids to display names.
    parseModifierIds = (value) => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) { /* not JSON — treat as a plain comma list */ }
            return value.split(',').map(id => id.trim()).filter(Boolean);
        }
        return [];
    };

    // Turns a stored modifier-id value back into display names ("Extra
    // Cheese, No Onion") via the Modifier master — used for rows that don't
    // carry the richer __modifiersLabel MenuList.js builds at add-time (i.e.
    // anything that came back from a fetch: rehydrated rows, another
    // device's rows, or this order's own confirmed/KOT-status rows).
    resolveModifiersLabel = (value) => {
        const ids = this.parseModifierIds(value);
        if (ids.length === 0) return '';
        const ModifierMaster = this.props.Modifier || [];
        return ids
            .map(id => {
                const found = ModifierMaster.find(m => m.value === id);
                return found ? found.label : id;
            })
            .join(', ');
    };

    // Builds this device's cart-item view from a Menu Items container's rows
    // — shared by addToCart/updateQty/removeFromCart/rehydrateFromResponse so
    // the row-identity (id/__rowIndex) logic can't drift out of sync between
    // them. The menu item id alone isn't a unique cart-row key any more (the
    // same dish can sit in the cart twice with different modifier choices,
    // or once with and once without), so the row's own array index is folded
    // into id — otherwise CartPanel's React keys collide and its +/-/delete
    // buttons resolve back to whichever of the two rows happens to be first.
    buildCartItemsFor = (stageFields, myName) => {
        const rows = stageFields[F.MENU_ITEMS].ContainerFields;
        return rows
            .map((row, idx) => ({ row, idx }))
            .filter(({ row }) =>
                row[F.MI_KOT_STATUS]?.FieldValue === "Pending" &&
                row[F.MI_ORDERED_BY]?.FieldValue === myName
            )
            .map(({ row, idx }) => ({
                id: `${row[F.MI_ITEM]?.FieldValue}__${idx}`,
                name: row.__displayName || "Unknown",
                price: Number(row[F.MI_RATE]?.FieldValue || 0),
                qty: Number(row[F.MI_QTY]?.FieldValue || 0),
                modifiersLabel: row.__modifiersLabel !== undefined
                    ? row.__modifiersLabel
                    : this.resolveModifiersLabel(row[F.MI_MODIFIER_SEL]?.FieldValue),
                __rowIndex: idx,
            }));
    };

    // ── Add item to cart ────────────────────────────────────────────────────
    addToCart = (item) => {
        // if (this.state.isOrderLocked) {
        //     if (this.growl) {
        //         this.growl.show({
        //             severity: "warn",
        //             detail: "Cannot add items to a confirmed order. Please create a new order."
        //         });
        //     }
        //     return;
        // }

        // Raw modifier ids selected for this add — this is the row's real
        // identity for merge/dedupe purposes and what gets persisted
        // (MI_MODIFIER_SEL, then the KOT action row's ModifierId). Kept as a
        // real array, not a joined string — MI_MODIFIER_SEL is a type-3
        // (MULTIDROPDOWN) field and this schema's save endpoint requires
        // that field's value to serialize as a JSON array (see
        // parseModifierIds/resolveModifiersLabel above). modifiersLabel is
        // the ready-made display string MenuList.js built from the same
        // selection ("Extra Cheese, No Onion x2") — kept only as this row's
        // __modifiersLabel for this session's own display, so the
        // per-modifier quantity survives without needing a second persisted
        // field (see resolveModifiersLabel for the fallback used once this
        // row round-trips through a save/reload).
        const modifierIds = Array.isArray(item.modifierIds) ? item.modifierIds : [];
        const modifiersLabel = item.modifiersLabel || '';
        const modifierIdsKey = JSON.stringify(modifierIds);

        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;

            // Scoped to this device's own pending rows only — otherwise
            // adding an item another guest already has pending would bump
            // their row's quantity instead of creating this device's own.
            // Also scoped to the same modifier selection — ordering the same
            // dish again with a different (or no) set of add-ons must land
            // as its own cart line, not bump an unrelated row's quantity.
            const myName = this.getEffectiveGuestName();
            const existingIndex = rows.findIndex(
                row =>
                    row[F.MI_ITEM].FieldValue === item.id &&
                    row[F.MI_KOT_STATUS].FieldValue === "Pending" &&
                    row[F.MI_ORDERED_BY]?.FieldValue === myName &&
                    JSON.stringify(row[F.MI_MODIFIER_SEL]?.FieldValue || []) === modifierIdsKey
            );

            let stageFields;

            if (existingIndex > -1) {
                const currentQty = Number(
                    rows[existingIndex][F.MI_QTY].FieldValue || 0
                );
                stageFields = updateContainerFieldValue(
                    prev.stageFields,
                    F.MENU_ITEMS,
                    existingIndex,
                    F.MI_QTY,
                    currentQty + 1
                );
            } else {
                const row = buildMenuItemRow();
                row[F.MI_ITEM] = {
                    ...row[F.MI_ITEM],
                    FieldValue: item.id
                };
                row[F.MI_RATE] = {
                    ...row[F.MI_RATE],
                    FieldValue: item.price
                };
                row[F.MI_QTY] = {
                    ...row[F.MI_QTY],
                    FieldValue: 1
                };
                row[F.MI_KITCHEN] = {
                    ...row[F.MI_KITCHEN],
                    FieldValue: item.kitchen || ''
                };
                row[F.MI_UOM] = {
                    ...row[F.MI_UOM],
                    FieldValue: item.uom || ''
                };
                row[F.MI_KOT_STATUS] = {
                    ...row[F.MI_KOT_STATUS],
                    FieldValue: "Pending"
                };
                row[F.MI_MODIFIER_SEL] = {
                    ...row[F.MI_MODIFIER_SEL],
                    FieldValue: modifierIds
                };
                // Per-item attribution for the multi-device shared order —
                // whoever's device added this item. Also doubles as the cart
                // privacy boundary (see the cartItems filters below): each
                // device only ever shows its own still-pending items.
                row[F.MI_ORDERED_BY] = {
                    ...row[F.MI_ORDERED_BY],
                    FieldValue: this.getEffectiveGuestName()
                };
                row.__displayName = item.name;
                row.__modifiersLabel = modifiersLabel;
                row.__isNew = true;

                stageFields = addContainerRow(
                    prev.stageFields,
                    F.MENU_ITEMS,
                    row
                );
            }

            // cartItems always mirrors THIS device's own still-Pending rows
            // only: Pending (drops out for everyone the instant
            // confirmOrder() flips it to Sent) AND owned by this device
            // (MI_ORDERED_BY match — another guest's still-pending items
            // stay private to their own cart until sent to the kitchen, at
            // which point everyone sees them under Order Status). __isNew is
            // deliberately not used here — confirmOrder() clears it before
            // the row is actually confirmed, and rehydration resets it on
            // every load, so it's not a reliable signal for either
            // "confirmed" or "mine".
            return {
                stageFields,
                hasUnconfirmedChanges: true,
                cartItems: this.buildCartItemsFor(stageFields, myName),
            };
        });
    };

    // ── Update quantity ────────────────────────────────────────────────────
    updateQty = (rowIndex, delta) => {
        if (false) {
            if (this.growl) {
                this.growl.show({
                    severity: "warn",
                    detail: "Cannot modify quantities of confirmed orders."
                });
            }
            return;
        }

        this.setState(prev => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;
            const row = rows[rowIndex];
            const myName = this.getEffectiveGuestName();

            if (row[F.MI_KOT_STATUS].FieldValue !== "Pending") {
                if (this.growl) {
                    this.growl.show({
                        severity: "warn",
                        detail: "Cannot modify confirmed items."
                    });
                }
                return null;
            }

            // Cart items are only ever rendered from this device's own rows
            // (see the cartItems filter below), so this should never fire in
            // practice — a defensive backstop against a stale index rather
            // than a real expected path.
            if (row[F.MI_ORDERED_BY]?.FieldValue !== myName) {
                return null;
            }

            const newQty = Number(row[F.MI_QTY].FieldValue || 0) + delta;
            let stageFields;

            if (newQty <= 0) {
                stageFields = removeContainerRow(
                    prev.stageFields,
                    F.MENU_ITEMS,
                    rowIndex
                );
            } else {
                stageFields = updateContainerFieldValue(
                    prev.stageFields,
                    F.MENU_ITEMS,
                    rowIndex,
                    F.MI_QTY,
                    newQty
                );
            }

            return {
                stageFields,
                hasUnconfirmedChanges: true,
                cartItems: this.buildCartItemsFor(stageFields, myName),
            };
        });
    };

    // ── Remove from cart ──────────────────────────────────────────────────
    removeFromCart = (rowIndex) => {
        // if (this.state.isOrderLocked) {
        //     if (this.growl) {
        //         this.growl.show({
        //             severity: "warn",
        //             detail: "Cannot remove items from confirmed orders."
        //         });
        //     }
        //     return;
        // }

        this.setState(prev => {
            const row = prev.stageFields[F.MENU_ITEMS].ContainerFields[rowIndex];
            const myName = this.getEffectiveGuestName();

            if (row[F.MI_KOT_STATUS].FieldValue !== "Pending") {
                if (this.growl) {
                    this.growl.show({
                        severity: "warn",
                        detail: "Cannot remove confirmed items."
                    });
                }
                return null;
            }

            // Defensive backstop, same reasoning as updateQty — cartItems
            // only ever surfaces this device's own rows, so a mismatch here
            // shouldn't happen in practice.
            if (row[F.MI_ORDERED_BY]?.FieldValue !== myName) {
                return null;
            }

            const stageFields = removeContainerRow(
                prev.stageFields,
                F.MENU_ITEMS,
                rowIndex
            );

            return {
                stageFields,
                hasUnconfirmedChanges: true,
                cartItems: this.buildCartItemsFor(stageFields, myName),
            };
        });
    };

    // ── Calculate grand total ────────────────────────────────────────────
    getGrandTotal = () => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        return rows.reduce((sum, r) => {
            const qty = Number(r[F.MI_QTY].FieldValue) || 0;
            const rate = Number(r[F.MI_RATE].FieldValue) || 0;
            return sum + (qty * rate);
        }, 0);
    };

    // ── Confirm Order ──────────────────────────────────────────────────────
    confirmOrder = () => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;

        const pendingRows = rows.filter(
            row => row[F.MI_KOT_STATUS].FieldValue === "Pending"
        );

        if (pendingRows.length === 0) {
            if (this.growl) {
                this.growl.show({
                    severity: "error",
                    detail: "Add at least one item before confirming"
                });
            }
            return;
        }

        const updatedRows = rows.map(row => {
            if (row[F.MI_KOT_STATUS].FieldValue === "Pending") {
                return {
                    ...row,
                    [F.MI_KOT_STATUS]: {
                        ...row[F.MI_KOT_STATUS],
                        FieldValue: "Sent"
                    }
                };
            }
            return row;
        });

        // this.setState(prev => ({
        //     stageFields: {
        //         ...prev.stageFields,
        //         [F.MENU_ITEMS]: {
        //             ...prev.stageFields[F.MENU_ITEMS],
        //             ContainerFields: updatedRows
        //         }
        //     },
        //     orderConfirmed: true,
        //     hasUnconfirmedChanges: false,
        //     sections: {
        //         status: true,
        //         menu: false,
        //         pay: false
        //     },
        //     isOrderLocked: true,
        //     shouldShowInList: false
        // }), () => {
        //     this.saveRequest('submit', 'confirm');
        // });

        // this.setState(prev => ({
        //     stageFields: {
        //         ...prev.stageFields,
        //         [F.MENU_ITEMS]: {
        //             ...prev.stageFields[F.MENU_ITEMS],
        //             ContainerFields: updatedRows
        //         }
        //     },
        //     orderConfirmed: true,
        //     hasUnconfirmedChanges: false,
        //     sections: {
        //         status: true,
        //         menu: false,
        //         pay: false
        //     },
        //     isOrderLocked: true,
        //     shouldShowInList: true
        // }), () => {

        //     // clear cart view after confirmation
        //     this.setState({
        //         hasUnconfirmedChanges: false
        //     });

        //     this.saveRequest('submit', 'confirm');
        // });

        this.setState(prev => {

            // const sentRows = prev.stageFields[F.MENU_ITEMS].ContainerFields.map(row => {

            //     if (row[F.MI_KOT_STATUS].FieldValue === "Pending") {
            //         return {
            //             ...row,
            //             [F.MI_KOT_STATUS]: {
            //                 ...row[F.MI_KOT_STATUS],
            //                 FieldValue: "Sent"
            //             }
            //         };
            //     }

            //     return row;
            // });
            const sentRows = prev.stageFields[F.MENU_ITEMS].ContainerFields.map(row => {

                if (row[F.MI_KOT_STATUS].FieldValue === "Pending") {
                    return {
                        ...row,
                        __isNew: false,
                        [F.MI_KOT_STATUS]: {
                            ...row[F.MI_KOT_STATUS],
                            FieldValue: "Sent"
                        }
                    };
                }

                return row;
            });
            return {
                stageFields: {
                    ...prev.stageFields,
                    [F.MENU_ITEMS]: {
                        ...prev.stageFields[F.MENU_ITEMS],
                        ContainerFields: sentRows
                    }
                },
                cartItems: [],      // Clear cart
                isOrderLocked: false,
                hasUnconfirmedChanges: false,
                sections: {
                    status: true,
                    menu: false,
                    pay: false
                }
            };

        }, () => {
            this.saveRequest("submit", "confirm");
        });
    };

    // ── Status Resolution ─────────────────────────────────────────────────
    static RESSTATUS_MASTER = [
        { Id: 15, ActionId: 0, ActionName: 'ORDER', StatusId: 1, StatusName: 'NEW', Sequence: 1 },
        { Id: 16, ActionId: 0, ActionName: 'ORDER', StatusId: 2, StatusName: 'CONFIRMED', Sequence: 2 },
        { Id: 18, ActionId: 0, ActionName: 'ORDER', StatusId: 3, StatusName: 'APPROVED', Sequence: 4 },
        { Id: 17, ActionId: 0, ActionName: 'ORDER', StatusId: 999, StatusName: 'CLOSED', Sequence: 3 },

        { Id: 0, ActionId: 1, ActionName: 'KOT', StatusId: 10, StatusName: 'CONFIRMED BY CUSTOMER', Sequence: 0 },
        { Id: 1, ActionId: 1, ActionName: 'KOT', StatusId: 11, StatusName: 'SENT TO KOT', Sequence: 1 },
        { Id: 2, ActionId: 1, ActionName: 'KOT', StatusId: 12, StatusName: 'ACCEPTED', Sequence: 2 },
        { Id: 3, ActionId: 1, ActionName: 'KOT', StatusId: 13, StatusName: 'PREPARING', Sequence: 3 },
        { Id: 4, ActionId: 1, ActionName: 'KOT', StatusId: 14, StatusName: 'READY', Sequence: 4 },
        { Id: 5, ActionId: 1, ActionName: 'KOT', StatusId: 15, StatusName: 'CLOSED', Sequence: 5 },
        { Id: 18, ActionId: 1, ActionName: 'KOT', StatusId: 16, StatusName: 'PAUSED', Sequence: 6 },
        { Id: 19, ActionId: 1, ActionName: 'KOT', StatusId: 17, StatusName: 'CANCELLED', Sequence: 7 },
        { Id: 6, ActionId: 2, ActionName: 'PACKING', StatusId: 21, StatusName: 'ASSIGNED', Sequence: 1 },
        { Id: 7, ActionId: 2, ActionName: 'PACKING', StatusId: 22, StatusName: 'PACKED', Sequence: 2 },
        { Id: 25, ActionId: 2, ActionName: 'PACKING', StatusId: 23, StatusName: 'DISPATCHED', Sequence: 3 },
        { Id: 8, ActionId: 3, ActionName: 'DELIVERY', StatusId: 31, StatusName: 'OUT FOR DELIVERY', Sequence: 1 },
        { Id: 9, ActionId: 3, ActionName: 'DELIVERY', StatusId: 32, StatusName: 'DELIVERED', Sequence: 2 },
        { Id: 26, ActionId: 3, ActionName: 'DELIVERY', StatusId: 33, StatusName: 'RETURNED', Sequence: 3 },
        { Id: 27, ActionId: 3, ActionName: 'DELIVERY', StatusId: 34, StatusName: 'FAILED', Sequence: 4 },
        { Id: 28, ActionId: 3, ActionName: 'DELIVERY', StatusId: 35, StatusName: 'CANCLLED', Sequence: 5 },
        { Id: 10, ActionId: 4, ActionName: 'BILL', StatusId: 41, StatusName: 'BILL GENERATED', Sequence: 1 },
        { Id: 29, ActionId: 4, ActionName: 'BILL', StatusId: 42, StatusName: 'PRINTED', Sequence: 2 },
        { Id: 30, ActionId: 4, ActionName: 'BILL', StatusId: 43, StatusName: 'PAID', Sequence: 3 },
        { Id: 31, ActionId: 4, ActionName: 'BILL', StatusId: 44, StatusName: 'CANCLLED', Sequence: 4 },
        { Id: 32, ActionId: 4, ActionName: 'BILL', StatusId: 45, StatusName: 'VOIDED', Sequence: 5 },
        { Id: 11, ActionId: 5, ActionName: 'PAYMENT', StatusId: 51, StatusName: 'PENDING', Sequence: 1 },
        { Id: 12, ActionId: 5, ActionName: 'PAYMENT', StatusId: 52, StatusName: 'PARTIALLY PAID', Sequence: 2 },
        { Id: 33, ActionId: 5, ActionName: 'PAYMENT', StatusId: 53, StatusName: 'PAID', Sequence: 3 },
        { Id: 34, ActionId: 5, ActionName: 'PAYMENT', StatusId: 54, StatusName: 'REFUNDED', Sequence: 4 },
        { Id: 35, ActionId: 5, ActionName: 'PAYMENT', StatusId: 55, StatusName: 'CANCELLED', Sequence: 5 },
        { Id: 36, ActionId: 5, ActionName: 'PAYMENT', StatusId: 56, StatusName: 'FAILED', Sequence: 6 },
        { Id: 13, ActionId: 6, ActionName: 'TABLE', StatusId: 61, StatusName: 'TABLE LOCKED', Sequence: 1 },
        { Id: 14, ActionId: 6, ActionName: 'TABLE', StatusId: 62, StatusName: 'TABLE BOOKED', Sequence: 2 },
        { Id: 20, ActionId: 7, ActionName: 'MENUITEMKOT', StatusId: 71, StatusName: 'PENDING', Sequence: 1 },
        { Id: 21, ActionId: 7, ActionName: 'MENUITEMKOT', StatusId: 72, StatusName: 'ACCEPTED', Sequence: 2 },
        { Id: 22, ActionId: 7, ActionName: 'MENUITEMKOT', StatusId: 73, StatusName: 'COOKING', Sequence: 3 },
        { Id: 23, ActionId: 7, ActionName: 'MENUITEMKOT', StatusId: 74, StatusName: 'READY', Sequence: 4 },
        { Id: 24, ActionId: 7, ActionName: 'MENUITEMKOT', StatusId: 75, StatusName: 'SERVED', Sequence: 5 },
        { Id: 43, ActionId: 9, ActionName: 'TAKEAWAY', StatusId: 91, StatusName: 'WAITING', Sequence: 1 },
        { Id: 44, ActionId: 9, ActionName: 'TAKEAWAY', StatusId: 92, StatusName: 'PREPARING', Sequence: 2 },
        { Id: 45, ActionId: 9, ActionName: 'TAKEAWAY', StatusId: 93, StatusName: 'READY', Sequence: 3 },
        { Id: 46, ActionId: 9, ActionName: 'TAKEAWAY', StatusId: 94, StatusName: 'COLLECTED', Sequence: 4 },
        { Id: 47, ActionId: 9, ActionName: 'TAKEAWAY', StatusId: 95, StatusName: 'CANCELLED', Sequence: 5 },
    ];

    _resolveStatus = (actionId, statusId) => {
        return QrOrderDetails.RESSTATUS_MASTER.find(
            r => r.ActionId === actionId && r.StatusId === statusId
        ) || null;
    };

    _buildBaseActionRow = (actionNum, resStatusRow, activeStatusId, targetDate) => ({
        Actionnum: actionNum,
        ActionId: resStatusRow.ActionId,
        actiontype: resStatusRow.ActionId,
        ActionName: resStatusRow.ActionName,
        ResStatusId: resStatusRow.Id,
        StatusId: activeStatusId,
        StatusName: resStatusRow.StatusName,
        Sequence: resStatusRow.Sequence,
        ActionDescription: resStatusRow.ActionName,
        ActionCode: resStatusRow.ActionName,
        Targetdate: moment(targetDate).toISOString(),
        // QR orders have no separate delivery date input — stamped with the
        // action's own date (today) so the Captain Dashboard can filter KOTs
        // by delivery date the same way as special/delivery orders.
        DeliveryDate: moment(targetDate).format('MM/DD/YYYY'),
        companyId: this.props.loggedUserDetail.CompanyId,
        // Guest display name for this KOT action — set per-row in
        // buildMenuItemRows from that row's own MI_ORDERED_BY, since one
        // confirm batch can bundle items different guests on the shared
        // order added. Defaults to null for non-menu-item action rows
        // (table lock, etc.) where there's no single item to attribute.
        pqeacr: null,
        // NEW the first time an item is declared in a KOT action row, OLD on
        // any later save that re-declares it — set per-row in
        // buildMenuItemRows via isNewMenuItem. Same null default as pqeacr
        // for non-menu-item action rows.
        pqejrer: null,
        AssignedTo: null,
        AssignedByEmployeeId: null,
        DeligateToEmployeeId: null,
        DelicateToEmpEmailId: null,
        DeligateFromDate: null,
        DeligateToDate: null,
        Comments: null,
        OrderType: null,
        Branch: null,
        Waiter: null,
        Cashier: null,
        Shift: null,
        TokenNumber: null,
        BillNumber: null,
        TableNumber: null,
        TableId: null,
        TableSubCode: null,
        AdultCount: null,
        ChildCount: null,
        ReservationNumber: null,
        TableStatus: null,
        CustomerName: null,
        CustomerMobileNumber: null,
        CustomerMembershipNo: null,
        CustomerGstNo: null,
        CustomerRemarks: null,
        DeliveryAddress: null,
        DeliveryLandmark: null,
        DeliveryGoogleLocation: null,
        DeliveryBuilding: null,
        DeliveryFloor: null,
        DeliveryDoor: null,
        DeliveryBoy: null,
        DeliveryCharge: null,
        ExpectedDeliveryTime: null,
        Distance: null,
        DeliveryStatus: null,
        DeliveryInstruction: null,
        MenuId: null,
        KitchenId: null,
        KOTNumber: null,
        KOTInstruction: null,
        ModifierId: null,
        MenuQty: null,
        ModifierQty: null,
        MenuUOM: null,
        ModifierUOM: null,
        MenuItemRate: null,
        MenuItemDiscountPercentage: null,
        MenuItemTaxPercentage: null,
        MenuItemNetAmount: null,
        MenuItemKitchenId: null,
        MenuItemSpecialInstruction: null,
        MenuItemKotStatus: null,
        MenuItemServedQty: null,
        MenuItemPreparedQty: null,
        MenuItemBalanceQty: null,
        MenuItemCancelledQty: null,
        BillHeaderGrossAmount: null,
        BillHeaderItemDiscount: null,
        BillHeaderBillDiscount: null,
        BillHeaderTax: null,
        BillHeaderServiceCharge: null,
        BillHeaderPackingCharge: null,
        BillHeaderDeliveryCharge: null,
        BillHeaderRoundOff: null,
        BillHeaderGrandTotal: null,
        PaymentType: null,
        PaymentAmountTendered: null,
        PaymentChangeReturn: null,
        PaymentTransactionNumber: null,
        PaymentCardNumber: null,
        PaymentBank: null,
        PaymentApprovalNo: null,
        PaymentStatus: null,
    });

    _buildOrderHeaderFields = (stageFields) => {
        const orderInfo = stageFields[F.ORDER_INFO]?.ContainerFields?.[0];
        const tableInfo = stageFields[F.TABLE_INFO]?.ContainerFields?.[0];
        const customerInfo = stageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        return {
            OrderType: stageFields[F.ORDER_TYPE]?.FieldValue || null,
            Branch: this.props.loggedUserDetail.defaultbranch || '00000000-0000-0000-0000-000000000001',
            Waiter: orderInfo?.[F.WAITER]?.FieldValue || null,
            Cashier: orderInfo?.[F.CASHIER]?.FieldValue || null,
            Shift: orderInfo?.[F.SHIFT]?.FieldValue || null,
            TokenNumber: orderInfo?.[F.TOKEN_NUMBER]?.FieldValue || null,
            BillNumber: stageFields[F.BILL_NUMBER]?.FieldValue || null,
            TableNumber: tableInfo?.[F.TABLE_NUMBER]?.FieldValue || null,
            // No F.TABLE_ID field exists in RequestAttributes (see claimTable) —
            // source straight from component state, which is where this was
            // always actually known.
            TableId: this.state.tableId || null,
            TableSubCode: tableInfo?.[F.TABLE_SUB_CODE]?.FieldValue || null,
            AdultCount: tableInfo?.[F.ADULT_COUNT]?.FieldValue ?? null,
            ChildCount: tableInfo?.[F.CHILD_COUNT]?.FieldValue ?? null,
            ReservationNumber: tableInfo?.[F.RESERVATION_NUMBER]?.FieldValue || null,
            TableStatus: tableInfo?.[F.TABLE_STATUS]?.FieldValue || null,
            CustomerName: customerInfo?.[F.CUSTOMER_NAME]?.FieldValue || null,
            CustomerMobileNumber: customerInfo?.[F.CUSTOMER_MOBILE]?.FieldValue || null,
            CustomerMembershipNo: customerInfo?.[F.CUSTOMER_MEMBERSHIP]?.FieldValue || null,
            CustomerGstNo: customerInfo?.[F.CUSTOMER_GST]?.FieldValue || null,
            CustomerRemarks: customerInfo?.[F.CUSTOMER_REMARKS]?.FieldValue || null,
        };
    };

    _buildBillHeaderFields = (stageFields) => {
        const b = stageFields[F.BILL_SUMMARY]?.ContainerFields?.[0];
        if (!b) return {};
        return {
            BillHeaderGrossAmount: b[F.BILL_GROSS]?.FieldValue ?? null,
            BillHeaderItemDiscount: b[F.BILL_ITEM_DISCOUNT]?.FieldValue ?? null,
            BillHeaderBillDiscount: b[F.BILL_DISCOUNT]?.FieldValue ?? null,
            BillHeaderTax: b[F.BILL_TAX]?.FieldValue ?? null,
            BillHeaderServiceCharge: b[F.BILL_SERVICE_CHARGE]?.FieldValue ?? null,
            BillHeaderPackingCharge: b[F.BILL_PACKING_CHARGE]?.FieldValue ?? null,
            BillHeaderDeliveryCharge: b[F.BILL_DELIVERY_CHARGE]?.FieldValue ?? null,
            BillHeaderRoundOff: b[F.BILL_ROUND_OFF]?.FieldValue ?? null,
            BillHeaderGrandTotal: b[F.BILL_GRAND_TOTAL]?.FieldValue ?? null,
        };
    };

    buildRequestActions = (actionContext, requestId, stageFields = {}, requestDate = new Date()) => {
        const orderHeader = this._buildOrderHeaderFields(stageFields);
        const existingActions = this.state.requestActionsRaw || [];
        // Same MenuId+KitchenId match RestaurantOrder.js uses for its own
        // priorKotNumber/priorTargetdate carry-forward — 'NEW' the first time
        // an item is declared in a KOT action row, 'OLD' on any later save
        // that re-declares it. existingActions rows are backend-echoed
        // (data.requestactions), so the key can come back as MenuId, menuId,
        // or menuid depending on the endpoint — same reason pick() exists
        // above for the allItems render mapping — so it's used here too.
        const isNewMenuItem = (row) => !existingActions.some(a =>
            pick(a, 'MenuId', 'menuId', 'menuid') === row[F.MI_ITEM]?.FieldValue &&
            pick(a, 'KitchenId', 'kitchenId', 'kitchenid') === row[F.MI_KITCHEN]?.FieldValue
        );

        const buildMenuItemRows = (menuRows, resStatusRow, requestDate, orderHeader, billFields, extraFields = {}) => {
            return menuRows.map((row, idx) => {
                const modifierIds = this.parseModifierIds(row[F.MI_MODIFIER_SEL]?.FieldValue);
                return {
                    ...this._buildBaseActionRow(idx + 1, resStatusRow, resStatusRow.StatusId, requestDate),
                    ...orderHeader,
                    ...billFields,
                    ...extraFields,
                    pqeacr: row[F.MI_ORDERED_BY]?.FieldValue || null,
                    pqejrer: isNewMenuItem(row) ? 'NEW' : 'OLD',
                    MenuId: row[F.MI_ITEM]?.FieldValue || null,
                    KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                    MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                    MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                    MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                    MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                    MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                    MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                    MenuItemNetAmount: row[F.MI_NET_AMOUNT]?.FieldValue ?? null,
                    MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || null,
                    MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                    MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                    MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                    MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                    MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                    KOTNumber: null,
                    KOTInstruction: null,
                    // Raw selected modifier ids, same selection that sat in
                    // this row's MI_MODIFIER_SEL while it was still in the
                    // cart — carries the add-on selection onto the KOT
                    // action row so it isn't dropped once confirmed.
                    ModifierId: modifierIds.length ? modifierIds : null,
                    ModifierQty: null,
                    ModifierUOM: null,
                };
            }).sort((a, b) => {
                if (a.KitchenId < b.KitchenId) return -1;
                if (a.KitchenId > b.KitchenId) return 1;
                return a.Actionnum - b.Actionnum;
            });
        };

        if (actionContext === 'save') return [];

        if (actionContext === 'table') {
            const tableStatus = this._resolveStatus(6, 61);
            const newStatus = this._resolveStatus(0, 1);
            return [
                { ...this._buildBaseActionRow(1, tableStatus, tableStatus.StatusId, requestDate), ...orderHeader },
                { ...this._buildBaseActionRow(2, newStatus, newStatus.StatusId, requestDate), ...orderHeader },
            ];
        }

        // if (actionContext === 'confirm') {
        //     const confirmedStatus = this._resolveStatus(1, 10);
        //     const billFields = this._buildBillHeaderFields(stageFields);

        //     const allRows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];

        //     const confirmedRows = allRows.filter(row =>
        //         row[F.MI_KOT_STATUS]?.FieldValue !== 'Pending' ||
        //         row[F.MI_KOT_STATUS]?.FieldValue === 'Sent'
        //     );

        //     const newRows = buildMenuItemRows(
        //         confirmedRows,
        //         confirmedStatus,
        //         requestDate,
        //         orderHeader,
        //         billFields,
        //     );

        //     return [...existingActions, ...newRows];
        // }

        // if (actionContext === 'confirm') {
        //     const confirmedStatus = this._resolveStatus(1, 10);
        //     const billFields = this._buildBillHeaderFields(stageFields);

        //     const allRows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];

        //     // const confirmedRows = allRows.filter(row =>
        //     //     row[F.MI_KOT_STATUS]?.FieldValue !== 'Pending' ||
        //     //     row[F.MI_KOT_STATUS]?.FieldValue === 'Sent'
        //     // );

        //     const confirmedRows = allRows.filter(
        //         row =>
        //             row[F.MI_KOT_STATUS]?.FieldValue === "Sent"
        //         // && row.__isNew === false
        //     );

        //     const newRows = buildMenuItemRows(
        //         confirmedRows,
        //         confirmedStatus,
        //         requestDate,
        //         orderHeader,
        //         billFields,
        //     );

        //     return [...existingActions, ...newRows];
        // }

        // if (actionContext === 'confirm') {
        //     const confirmedStatus = this._resolveStatus(1, 10);
        //     const billFields = this._buildBillHeaderFields(stageFields);

        //     const allRows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];

        //     const confirmedRows = allRows.filter(
        //         row =>
        //             row[F.MI_KOT_STATUS]?.FieldValue === "Sent" &&
        //             row.__isNew === true
        //     );

        //     const newRows = buildMenuItemRows(
        //         confirmedRows,
        //         confirmedStatus,
        //         requestDate,
        //         orderHeader,
        //         billFields,
        //     );

        //     const validExistingActions = existingActions.filter(
        //         action =>
        //             action &&
        //             action.requestactionid &&
        //             action.requestactionid !== ZERO_GUID
        //     );

        //     return [...validExistingActions, ...newRows];
        // }

        // ```js
        if (actionContext === 'confirm') {
            // StatusId 10 = "CONFIRMED BY CUSTOMER" — pending with the
            // captain, not yet in the kitchen. The captain moves it to 11
            // ("SENT TO KOT") from the Captain Dashboard's QR Orders modal
            // (CaptainDashboard.js's sendQrItemsToKitchen) once reviewed.
            const confirmedStatus = this._resolveStatus(1, 10);
            const billFields = this._buildBillHeaderFields(stageFields);

            const allRows =
                stageFields[F.MENU_ITEMS]?.ContainerFields || [];

            // Rows this confirm just flipped Pending -> Sent that haven't
            // already had a KOT action row persisted for them. Not __isNew —
            // confirmOrder() clears that before this even runs, so it can
            // never distinguish "just confirmed" from "confirmed earlier"
            // (see __kotActionSent, set once the save actually succeeds).
            const confirmedRows = allRows.filter(
                row =>
                    row[F.MI_KOT_STATUS]?.FieldValue === "Sent" &&
                    !row.__kotActionSent
            );

            const newRows = buildMenuItemRows(
                confirmedRows,
                confirmedStatus,
                requestDate,
                orderHeader,
                billFields,
            );

            console.log("New actions being sent:", newRows);

            return newRows;
        }



        return existingActions;
    };

    // Narrow, read-only counterpart to rehydrateFromResponse — extracts just
    // the Menu Items container rows from a raw RequestAttributesDetails
    // response. Deliberately not a shared refactor of rehydrateFromResponse
    // itself: that function also drives the main load path's setState, and
    // this only ever needs one container's rows for the merge below.
    extractMenuItemRows = (data) => {
        const stageList = data?.requestAttribute1?.[0];
        const menuField = (stageList?.stage_Field || []).find(f => f.fieldDescription === 'Menu Items');
        if (!menuField || !menuField.stage_FieldsList) return [];

        return menuField.stage_FieldsList.map(conFields => {
            const rowObj = {};

            (conFields.stage_FieldsListc2 || []).forEach(conField => {
                const childKey = 'field_' + conField.fieldNum;

                let childValue = conField.fieldValue;
                if (conField.fieldType === 8 || conField.fieldType === '8') {
                    childValue = new Date(conField.fieldValue);
                } else if (conField.fieldType === 10 || conField.fieldType === '10') {
                    childValue = conField.fieldValue === 'true';
                }

                rowObj[childKey] = {
                    FieldName: conField.fieldNum,
                    FieldLabel: conField.fieldDescription,
                    FieldValue: childValue,
                    FieldType: conField.fieldType,
                    DataType: conField.dataType,
                    ParentFieldId: conField.parentFieldNum,
                    ChildRowNmbr: conField.childRowNmbr,
                    FieldId: conField.fieldId || '',
                    type: conField.containerType,
                };
            });

            const menuItemId = conFields.stage_FieldsListc2?.find(
                f => f.fieldDescription === 'Menu Item'
            )?.fieldValue;
            if (menuItemId) {
                const found = (this.props.menuitems || []).find(m => m.menuItemId === menuItemId);
                rowObj.__displayName = found ? found.menuName : menuItemId;
            }
            rowObj.__isNew = false;
            // Anything the server already has beyond Pending was confirmed
            // by whichever device did it first — without this, the
            // 'confirm' actionContext's __kotActionSent check (see
            // buildRequestActions) wouldn't know that and would recreate a
            // duplicate KOT action row for an item another device already
            // sent.
            const kotStatus = rowObj[F.MI_KOT_STATUS]?.FieldValue;
            rowObj.__kotActionSent = kotStatus && kotStatus !== 'Pending';
            return rowObj;
        });
    };

    // Multi-device concurrency mitigation (see the QR ordering plan's §5c):
    // saveRequest POSTs this device's whole local snapshot, which is a
    // last-write-wins race when a family is adding items from several phones
    // at once. Right before the highest-risk save — 'confirm', dispatching
    // pending items to the kitchen — refetch what's actually saved on the
    // server so _performSave can union in any row another device saved that
    // this one doesn't know about yet, instead of silently overwriting it.
    // Narrows the race window to the few hundred ms between this GET and the
    // POST that follows; doesn't eliminate it (a real fix needs the backend
    // to accept per-item appends, out of scope here).
    mergeLatestMenuItemsBeforeSave = (requestId) => {
        // Same cache key getMasterTableList uses for this endpoint — has to
        // be cleared or this GET just replays whatever was cached earlier.
        localStorage.removeItem('Employee/RequestAttributesDetails?Id=' + requestId);

        return axios.get('/master/api/Employee/RequestAttributesDetails?Id=' + requestId)
            .then(response => this.extractMenuItemRows(response.data))
            .catch(() => null); // refetch failed — fall back to this device's own snapshot
    };

    // saveRequest = (type, redirect = '') => {
    //     if (this.state.isSaving) return;
    //     this.setState({ isSaving: true, loading: true });
    saveRequest = (type, redirect = '') => {
        if (this.isSavingLock || this.state.isSaving) {
            console.log("Save already in progress. Ignoring duplicate request.");
            return;
        }

        this.isSavingLock = true;

        this.setState({
            isSaving: true,
            loading: true
        });

        const isValidRequestId = (id) => id && id !== ZERO_GUID;
        const requestId = isValidRequestId(this.state.DynamicRequestID) ? this.state.DynamicRequestID : null;
        const actionContext = redirect || type;

        const mergePromise = (actionContext === 'confirm' && requestId)
            ? this.mergeLatestMenuItemsBeforeSave(requestId)
            : Promise.resolve(null);

        mergePromise.then(latestMenuRows => {
            this._performSave(type, redirect, latestMenuRows);
        });
    };

    _performSave = (type, redirect = '', latestMenuRows = null) => {
        const { stageFields: localStageFields } = this.state;

        // Union, never replace: keep every row this device currently has
        // (whatever state it's in — __isNew isn't a safe signal here, since
        // confirmOrder() already clears it the moment a row flips
        // Pending -> Sent, before this save even fires), and only ADD
        // server-side rows this device doesn't know about yet — i.e. items
        // another device added/confirmed in the meantime. A row only ever
        // gets a real FieldId once the backend has persisted it, so a
        // brand-new local row can never spuriously match a server row and
        // get excluded; this can add a row this device is missing, but can
        // never drop one it already has.
        const localRows = localStageFields[F.MENU_ITEMS]?.ContainerFields || [];
        const localFieldIds = new Set(
            localRows.map(row => row[F.MI_ITEM]?.FieldId).filter(id => id)
        );
        const serverOnlyRows = (latestMenuRows || []).filter(row => {
            const fieldId = row[F.MI_ITEM]?.FieldId;
            return fieldId && !localFieldIds.has(fieldId);
        });

        const stageFields = latestMenuRows
            ? {
                ...localStageFields,
                [F.MENU_ITEMS]: {
                    ...localStageFields[F.MENU_ITEMS],
                    ContainerFields: [...localRows, ...serverOnlyRows],
                },
            }
            : localStageFields;

        let fieldSequence = 1;
        let RequestType = '';
        let RequestedFor = this.props.loggedUserDetail && this.props.loggedUserDetail.empID;
        let RequestDate = new Date();
        let FromDate = new Date();
        let ToDate = new Date();

        const isValidRequestId = (id) => id && id !== ZERO_GUID;
        // Same requestId the outer saveRequest already resolved (it's what
        // decided whether to merge in the first place) — recomputed here
        // since _performSave still needs it in scope for buildGroupAttributes.
        let requestId = isValidRequestId(this.state.DynamicRequestID) ? this.state.DynamicRequestID : null;
        const actionContext = redirect || type;

        const total = this.getGrandTotal();
        const syncedStageFields = updateContainerFieldValue(stageFields, F.BILL_SUMMARY, 0, F.BILL_GRAND_TOTAL, total);

        Object.values(syncedStageFields).forEach(field => {
            if (field.FieldName === raw(F.ORDER_TYPE)) RequestType = field.FieldValue;
            if (field.FieldName === raw(F.REQUEST_DATE)) RequestDate = field.FieldValue;
        });

        const serializeFieldValue = (fieldValue) => {
            if (fieldValue === null || fieldValue === undefined) return fieldValue;
            if (Array.isArray(fieldValue)) return JSON.stringify(fieldValue);
            if (typeof fieldValue === 'object') return JSON.stringify(fieldValue);
            return fieldValue;
        };

        // fieldKeys: optional whitelist of stageFields keys — sends just the
        // containers a given actionContext actually needs instead of the
        // full field set every time, same mechanism RestaurantOrder.js's own
        // saveRequest uses (see its buildGroupAttributes).
        const buildGroupAttributes = (fieldKeys = null) => {
            const groupAttributes = [];
            fieldSequence = 1;
            const isUpdate = isValidRequestId(requestId);

            const entries = fieldKeys
                ? fieldKeys.map(key => [key, syncedStageFields[key]]).filter(([, field]) => field)
                : Object.entries(syncedStageFields);

            entries.forEach(([key, field]) => {
                const commonValues = {
                    ChildRowNmbr: field.ChildRowNmbr,
                    DataType: field.DataType,
                    FieldDescription: field.FieldLabel,
                    FieldName: field.FieldName,
                    FieldType: field.FieldType,
                    FieldValue: serializeFieldValue(
                        (field.FieldType === 7 || field.FieldType === 8)
                            ? moment(field.FieldValue).format('MM/DD/YYYY')
                            : field.FieldValue
                    ),
                    ParentFieldId: field.ParentFieldId,
                    stageNum: STAGE_NUM,
                    processNum: PROCESS_NUM,
                    FieldSequence: fieldSequence++,
                    IsActive: true,
                    CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
                    StageSequence: 0,
                    ContainerType: field.type,
                    branch: "00000000-0000-0000-0000-000000000001"
                };

                if (isUpdate && field.FieldId !== undefined && field.FieldId !== '') {
                    commonValues.FieldId = field.FieldId;
                }

                groupAttributes.push(commonValues);

                if (field.ContainerFields === null) return;

                Object.entries(field.ContainerFields).forEach(([, containerField], containerIndex) => {
                    Object.entries(containerField)
                        .filter(([, f]) => f && typeof f === 'object' && f.FieldName)
                        .forEach(([, containerFField]) => {
                            const containerValues = {
                                ChildRowNmbr: containerIndex + 1,
                                DataType: containerFField.DataType,
                                FieldDescription: containerFField.FieldLabel,
                                FieldName: containerFField.FieldName,
                                FieldType: containerFField.FieldType,
                                FieldValue: serializeFieldValue(
                                    (containerFField.FieldType === 7 || containerFField.FieldType === 8)
                                        ? moment(containerFField.FieldValue).format('MM/DD/YYYY')
                                        : containerFField.FieldValue
                                ),
                                ParentFieldId: containerFField.ParentFieldId,
                                stageNum: STAGE_NUM,
                                processNum: PROCESS_NUM,
                                FieldSequence: fieldSequence++,
                                IsActive: true,
                                CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
                                StageSequence: 0,
                                ContainerType: field.type,
                            };

                            if (isUpdate && containerFField.FieldId !== undefined && containerFField.FieldId !== '') {
                                containerValues.FieldId = containerFField.FieldId;
                            }

                            groupAttributes.push(containerValues);
                        });
                });
            });

            return groupAttributes;
        };

        const isSave = (actionContext === 'save');
        // No existing backend order yet (claimTable's 'table' save always
        // lands here) — the insert that creates the request has to carry the
        // full field set or it plants an order with no table/customer/order-
        // type data at all. Same reasoning as RestaurantOrder.js's own
        // isInitialEntry check.
        const isInitialEntry = !requestId;

        const requestAttributes = (isSave || isInitialEntry)
            ? buildGroupAttributes()
            // 'confirm' (sending pending items to the kitchen): only the
            // containers that actually changed need to go with it — same
            // whitelist RestaurantOrder.js's own 'kitchen' actionContext
            // sends, since this is the same action under a different name.
            : actionContext === 'confirm'
                ? buildGroupAttributes([
                    F.MENU_ITEMS,
                    F.CUSTOMER_INFO,
                    F.TABLE_INFO,
                    F.ORDER_TYPE,
                    F.WAITER,
                    F.CASHIER,
                    F.SHIFT,
                    F.TOKEN_NUMBER,
                ])
                : [];
        // const requestActions = isSave
        //     ? []
        //     : this.buildRequestActions(actionContext, requestId, syncedStageFields, RequestDate);
        let requestActions = isSave
            ? []
            // Deliberately not passing RequestDate through here — that's this
            // order's original creation timestamp (set once when the order was
            // opened, then reused unchanged on every later save, e.g. when items
            // get added to an already-running order). Each action row's own
            // Targetdate must reflect when THIS action actually happened, so this
            // leans on buildRequestActions' own default of `new Date()` (now)
            // instead of the order's stale creation date.
            : this.buildRequestActions(actionContext, requestId, syncedStageFields);

        requestActions = requestActions.filter(
            action => action != null
        );

        // ── Restaurant order header (REQUESTHEADER Res* columns) ──────────────
        // Denormalised copies of values that otherwise live only inside the
        // attribute containers, so an order can be listed and reported on
        // without unpacking RequestAttributes.
        //
        // Read straight off the containers rather than through
        // _buildOrderHeaderFields: that helper reads F.CUSTOMER_NAME /
        // F.CUSTOMER_MOBILE, neither of which exists in RestaurantOrderFields —
        // they resolve to undefined and hand back null for every customer.
        // The real constants are F.CUST_NAME / F.CUST_MOBILE, used here.
        const resCustomerRow = syncedStageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        const resAddressRow = syncedStageFields[F.DELIVERY_ADDRESS]?.ContainerFields?.[0];
        const resPaymentRow = syncedStageFields[F.PAYMENT_INFO]?.ContainerFields?.[0];
        // A QR order is eaten in-house and has no delivery date of its own; the
        // column is a non-nullable DateTime, so it falls back to the order date.
        const resDeliveryDate = syncedStageFields[F.DELIVERY_DATE]?.FieldValue || RequestDate;

        const data = {
            RequestDesc: 'Restaurant Order',
            IsActive: true,
            RequestDate: moment(RequestDate).format('MM/DD/YYYY'),
            FromDate: moment(FromDate).format('MM/DD/YYYY'),
            ToDate: moment(ToDate).format('MM/DD/YYYY'),
            EmpID: this.props.loggedUserDetail && this.props.loggedUserDetail.empID,
            CompanyID: this.props.loggedUserDetail && this.props.loggedUserDetail.CompanyId,
            Remark: '',
            RequestedFor: RequestedFor,
            RequestType: RequestType,
            ProcessID: RESTAURANT_ORDER_ID.toUpperCase(),

            Resdeliverydate: moment(resDeliveryDate).format('MM/DD/YYYY'),
            Resdeliverytime: syncedStageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue || '',
            Resdeliveryaddress: resAddressRow?.[F.DEL_ADDRESS]?.FieldValue || '',
            Rescustomername: resCustomerRow?.[F.CUST_NAME]?.FieldValue || '',
            Resphone: resCustomerRow?.[F.CUST_MOBILE]?.FieldValue || '',
            // `total` is this save's freshly computed grand total, already
            // stamped into the Bill Summary row above — same number, one source.
            Restotalamount: total,
            Respaymentstatus: resPaymentRow?.[F.PAY_STATUS]?.FieldValue || '',

            RequestAttributes: requestAttributes,
            Requestattachments: [],
            Requestforms: [],
            Requestactions: requestActions,
            StageSequence: 0,
        };

        if (requestId != null) data.RequestID = requestId;

        let model = 'requestSave';
        let url = requestId
            ? '/master/api/UpdateDynamicData?model=' + model
            : '/master/api/InsertDynamicData?model=' + model;

        // axios.post(url, JSON.stringify({ Key: JSON.stringify(data) }))
        //     .then(response => {
        //         this.setState({
        //             loading: false,
        //             isSaving: false,
        //             stageFields: syncedStageFields
        //         });

        //         if (response.status === 200) {
        //             const returnedId = response.data?.requestId || this.state.DynamicRequestID;

        //             if (returnedId) {
        //                 this.setState({
        //                     DynamicRequestID: returnedId
        //                 }, () => {
        //                     if (this.state.shouldShowInList && this.props.userDetail?.employeeId) {
        //                         this.props.getOrderList(
        //                             this.props.userDetail.employeeId,
        //                             RESTAURANT_ORDER_ID,
        //                             this.props.loggedUserDetail.CompanyId
        //                         );
        //                     }
        //                     if (redirect === "confirm") {
        //                         this.props.history.push(
        //                             `/pos/qrorder/${this.state.tableId}/${this.state.tableNo}`
        //                         );
        //                     }
        //                 });
        //             }
        //         } else {
        //             this.growl && this.growl.show({
        //                 severity: "error",
        //                 summary: null,
        //                 detail: "Failed to save order"
        //             });
        //         }
        //     })
        //     .catch(err => {
        //         this.setState({ loading: false, isSaving: false });
        //         this.growl && this.growl.show({
        //             severity: "error",
        //             summary: null,
        //             detail: (err && err.response && err.response.data) || "Submit Failed"
        //         });
        //     });
        axios.post(url, JSON.stringify({ Key: JSON.stringify(data) }))
            .then(response => {

                if (response.status === 200) {
                    this.isSavingLock = false;

                    // Mark newly added items as existing/saved. Sent rows
                    // additionally get __kotActionSent — buildRequestActions'
                    // 'confirm' branch uses that (not __isNew, which
                    // confirmOrder() already clears before the save even
                    // starts) to know a KOT action row has genuinely been
                    // persisted for them, so a later confirm of newly-added
                    // items doesn't recreate duplicate KOT rows for these.
                    const updatedStageFields = {
                        ...syncedStageFields,
                        [F.MENU_ITEMS]: {
                            ...syncedStageFields[F.MENU_ITEMS],
                            ContainerFields: (
                                syncedStageFields[F.MENU_ITEMS]?.ContainerFields || []
                            ).map(row => ({
                                ...row,
                                __isNew: false,
                                __kotActionSent: row[F.MI_KOT_STATUS]?.FieldValue === "Sent"
                                    ? true
                                    : row.__kotActionSent,
                            }))
                        }
                    };

                    this.setState({
                        loading: false,
                        isSaving: false,
                        stageFields: updatedStageFields
                    });

                    const returnedId =
                        response.data?.requestId ||
                        response.data?.requestID ||
                        this.state.DynamicRequestID;

                    if (returnedId) {

                        this.setState({
                            DynamicRequestID: returnedId
                        }, () => {

                            if (
                                this.state.shouldShowInList &&
                                this.props.userDetail?.employeeId
                            ) {
                                this.props.getOrderList(
                                    this.props.userDetail.employeeId,
                                    RESTAURANT_ORDER_ID,
                                    this.props.loggedUserDetail.CompanyId
                                );
                            }

                            if (redirect === "confirm") {
                                this.props.history.push(
                                    `/pos/qrorder/${this.state.tableId}/${this.state.tableNo}`
                                );
                            }
                        });
                    }

                } else {
                    this.isSavingLock = false;


                    this.setState({
                        loading: false,
                        isSaving: false
                    });

                    this.growl && this.growl.show({
                        severity: "error",
                        summary: null,
                        detail: "Failed to save order"
                    });
                }

            })
            .catch(err => {
                this.isSavingLock = false;


                console.error("Save Request Error:", err);

                this.setState({
                    loading: false,
                    isSaving: false
                });

                this.growl && this.growl.show({
                    severity: "error",
                    summary: null,
                    detail:
                        err?.response?.data?.message ||
                        err?.response?.data ||
                        err?.message ||
                        "Submit Failed"
                });
            });
    };

    // ── Render ──────────────────────────────────────────────────────────────
    render() {
        const {
            stageFields, tableNo, tableId, sections,
            loading, isOrderLocked, guestName,
        } = this.state;
        const { menuitems } = this.props;

        if (!stageFields) {
            return <div className="pos-order-screen pos-order-screen-error">Loading…</div>;
        }

        // Order Status is built from the Requestactions/KOT action data
        // (this.state.requestActionsRaw), not RequestAttributes' Menu Items
        // container — that's where the item's real current kitchen status
        // lives (confirmed/sent/accepted/preparing/ready/closed), not just
        // the local Pending/Sent draft flag. A row with no MenuId is a
        // non-menu-item action (table lock, etc.) and is filtered out.
        const allItems = (this.state.requestActionsRaw || [])
            .map((row, idx) => {
                const menuId = pick(row, 'MenuId', 'menuId', 'menuid');
                const found = (menuitems || []).find(m => m.menuItemId === menuId);
                return {
                    id: menuId,
                    name: found ? found.menuName : (menuId || 'Unknown Item'),
                    price: Number(pick(row, 'MenuItemRate', 'menuItemRate', 'menuitemrate') || 0),
                    qty: Number(pick(row, 'MenuQty', 'menuQty', 'menuqty') || 0),
                    statusId: pick(row, 'StatusId', 'statusId', 'statusid'),
                    orderedBy: pick(row, 'pqeacr', 'Pqeacr', 'PQEACR') || '',
                    modifiersLabel: this.resolveModifiersLabel(
                        pick(row, 'ModifierId', 'modifierId', 'modifierid') || ''
                    ),
                    __rowIndex: pick(row, 'requestactionid', 'RequestActionId', 'Actionnum', 'actionnum') ?? idx,
                };
            })
            .filter(item => item.id);

        const cartItems = this.state.cartItems.map(item => ({
            ...item,
            price: Number(item.price || 0),
            qty: Number(item.qty || 0),
        }));

        // One row per confirmed menu item — still and served items sort to
        // the bottom, but everything's one flat, scannable list rather than
        // split across separate per-status sections.
        const sortedItems = [...allItems].sort(
            (a, b) => kotStatusMeta(a.statusId).order - kotStatusMeta(b.statusId).order
        );

        const grandTotal = this.getGrandTotal();

        return (
            <div className="qr-mobile-container">
                {loading && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />
                {this.props.error !== null && <Messages type="error" message={this.props.error} />}

                <div className="qr-order-header">
                    <Link className="qr-back-link" to={`/pos/qrorder/${this.state.tableId}/${this.state.tableNo}`}>‹ Orders</Link>
                    <span className="qr-order-table">Table {tableNo || '—'}</span>
                    {/* {isOrderLocked && (
                        <span className="qr-order-locked-badge">🔒 Confirmed</span>
                    )} */}
                </div>

                {/* Optional, per-device — shown on this device's own items in
                    the shared order so a family sharing one table's order can
                    tell who ordered what. Never blocks ordering. */}
                <div className="qr-guest-name">
                    <label htmlFor="qr-guest-name-input">
                        Your name (optional){this.isNameLocked() && ' — locked'}
                    </label>
                    <input
                        id="qr-guest-name-input"
                        type="text"
                        placeholder={this.state.deviceLabel}
                        value={guestName}
                        onChange={(e) => this.setGuestName(e.target.value)}
                        onBlur={this.commitGuestName}
                        disabled={this.isNameLocked()}
                        maxLength={40}
                    />
                </div>

                <Section
                    title="Order Status"
                    isOpen={sections.status}
                    onToggle={() => this.toggleSection('status')}
                    badge={allItems.length > 0 ? allItems.length : null}
                >
                    {sortedItems.length > 0 ? (
                        <>
                            <table className="qr-status-table">
                                <thead>
                                    <tr>
                                        <th className="col-name">Item</th>
                                        <th className="col-qty">Qty</th>
                                        <th className="col-price">Price</th>
                                        <th className="col-orderedby">Ordered By</th>
                                        <th className="col-status">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.map(item => {
                                        const meta = kotStatusMeta(item.statusId);
                                        return (
                                            <tr key={item.__rowIndex} className={`qr-status-row ${meta.className}`}>
                                                <td className="col-name">
                                                    {item.name}
                                                    {item.modifiersLabel && (
                                                        <span className="qr-item-modifiers">{item.modifiersLabel}</span>
                                                    )}
                                                </td>
                                                <td className="col-qty">{item.qty}</td>
                                                <td className="col-price">₹{Number(item.price || 0).toFixed(2)}</td>
                                                <td className="col-orderedby">{item.orderedBy || '—'}</td>
                                                <td className="col-status">
                                                    <span className={`qr-status-badge ${meta.className}`}>
                                                        {meta.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="qr-status-total">
                                Order total: ₹{Number(grandTotal || 0).toFixed(2)}                            </div>
                        </>
                    ) : (
                        <p className="qr-empty-note">No items ordered yet. Add items from the menu.</p>
                    )}
                </Section>

                <Section
                    title="Add Items"
                    isOpen={sections.menu}
                    onToggle={() => this.toggleSection('menu')}
                    badge={cartItems.length > 0 ? cartItems.length : null}
                >
                    <MenuList
                        onAddToCart={this.addToCart}
                        menuitems={menuitems}
                        disabled={false}
                    />
                </Section>

                {/* Not inside the "Add Items" collapsible — this device's
                    cart and Confirm Order action need to stay visible
                    whether or not that section is expanded. */}
                <CartPanel
                    cartItems={cartItems}
                    kotItems={[]}
                    hideKotSection
                    // isOrderLocked reflects the whole shared order (true
                    // the moment anyone confirms anything), not this
                    // specific row — cartItems only ever shows this
                    // device's own still-Pending items anyway, so
                    // updateQty/removeFromCart's own per-row Pending +
                    // ownership checks are the correct, right-scoped
                    // guard. Gating on isOrderLocked here silently
                    // blocked +/-/delete on brand-new items the moment
                    // anyone had confirmed anything, ever.
                    onUpdateQty={(itemId, delta) => {
                        const item = cartItems.find(i => i.id === itemId);
                        if (item) this.updateQty(item.__rowIndex, delta);
                    }}
                    onRemoveFromCart={(itemId) => {
                        const item = cartItems.find(i => i.id === itemId);
                        if (item) this.removeFromCart(item.__rowIndex);
                    }}
                    grandTotal={grandTotal}
                    isLocked={false}
                    actions={[
                        {
                            label: 'Confirm Order',
                            className: 'p-button-success',
                            onClick: this.confirmOrder,
                            disabled: cartItems.length === 0,
                        },
                    ]}
                />

                <Section
                    title="Bill & Pay"
                    isOpen={sections.pay}
                    onToggle={() => this.toggleSection('pay')}
                >
                    <div className="qr-pay-total">
                        Total: ₹{Number(grandTotal || 0).toFixed(2)}                    </div>
                    <div className="qr-pay-options">
                        <Button label="Pay by Cash" className="p-button-outlined pos-cart-action-btn" disabled />
                        <Button label="Pay by Card" className="p-button-outlined pos-cart-action-btn" disabled />
                        <Button label="Pay by UPI" className="p-button-outlined pos-cart-action-btn" disabled />
                    </div>
                    <p className="qr-pay-note">
                        Online payment coming soon — please pay at the counter for now.
                    </p>
                </Section>
            </div>
        );
    }
}

// ── Redux mappings ────────────────────────────────────────────────────────
const mapStateToProps = state => {
    return {
        error: state.master.error,
        loading: state.master.loading,
        loggedUserDetail: state.auth.userObject,
        tableLists: state.master.tableLists,
        menuitems: state.master.masterData?.GetinvMenuItemList || [],
        userDetail: state.auth.userDetail,
        Modifier: state.keyword.Modifier || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        getMasterKeywordList: (value) => dispatch(actions.getMasterKeywordList(value)),
        getMasterTableList: (table) => dispatch(actions.getMasterTableList(table)),
        getMasterData: (tableUrl) => dispatch(actions.getMasterData(tableUrl)),
        getOrderList: (id, processId, companyId) =>
            dispatch(actions.getrequestListgen(id, processId, companyId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(QrOrderDetails);